<div class="container">
  <div class="page-header">
    <h1>Overview</h1>
    <p class="subtitle">
      Spectyra is an AI optimization app for models and agents in production. Measure runs and replays, surface
      savings, and connect your stack so improvements are grounded in data—not guesswork.
    </p>
  </div>

  <div *ngIf="loading" class="loading">
    <p>Loading dashboard...</p>
  </div>

  <div *ngIf="error" class="error-box">
    <p>{{ error }}</p>
  </div>

  <div *ngIf="!loading && !error">
    <!-- Desktop app (logged-in users) -->
    <div class="card download-banner">
      <div class="download-banner-inner">
        <div class="download-banner-text">
          <h2>OpenClaw + local companion</h2>
          <p>
            Use Spectyra with OpenClaw via the ClawHub skill and the local companion — same account as the web app, no Spectyra Desktop required for this path.
          </p>
        </div>
        <a routerLink="/integrations/openclaw" class="btn btn-primary">Get started</a>
      </div>
    </div>

    <!-- Integration Status Card -->
    <div class="card">
      <div class="card-header">
        <h2>Integration Status</h2>
        <a routerLink="/integrations/openclaw" class="btn btn-sm btn-secondary">Manage</a>
      </div>
      
      <div *ngIf="!hasIntegrations" class="empty-state">
        <h3>No integrations configured</h3>
        <p>Get started by adding an integration to begin using Spectyra.</p>
        <div class="integration-tiles">
          <div class="integration-tile" routerLink="/integrations/openclaw">
            <div class="tile-icon"><span class="material-icons">smart_toy</span></div>
            <h4>OpenClaw</h4>
            <p>Skill + local companion on your machine</p>
          </div>
        </div>
        <a routerLink="/integrations/openclaw" class="btn btn-primary">OpenClaw integration guide</a>
      </div>

      <div *ngIf="hasIntegrations && integrationStatus" class="integration-status">
        <div class="status-grid">
          <div class="status-item">
            <span class="status-label">SDK (Local)</span>
            <span class="status-badge" [class.active]="integrationStatus.sdk_local">
              {{ integrationStatus.sdk_local ? 'Connected' : 'Not Connected' }}
            </span>
          </div>
          <div class="status-item">
            <span class="status-label">SDK (API)</span>
            <span class="status-badge" [class.active]="integrationStatus.sdk_remote">
              {{ integrationStatus.sdk_remote ? 'Connected' : 'Not Connected' }}
            </span>
          </div>
          <div class="status-item">
            <span class="status-label">API Gateway</span>
            <span class="status-badge" [class.active]="integrationStatus.api">
              {{ integrationStatus.api ? 'Connected' : 'Not Connected' }}
            </span>
          </div>
        </div>
        <div class="status-meta">
          <p *ngIf="integrationStatus.last_event_at">
            Last event: {{ formatDate(integrationStatus.last_event_at) }}
          </p>
          <p *ngIf="integrationStatus.last_run_at">
            Last run: {{ formatDate(integrationStatus.last_run_at) }}
          </p>
        </div>
      </div>
    </div>

    <!-- Last 24h Usage -->
    <div class="card" *ngIf="usage24h">
      <div class="card-header">
        <h2>Last 24 Hours</h2>
        <a routerLink="/analytics" class="btn btn-sm btn-secondary">Session analytics</a>
      </div>
      <div class="usage-grid">
        <div class="usage-item">
          <span class="usage-label">Calls</span>
          <span class="usage-value">{{ formatNumber(usage24h.calls) }}</span>
        </div>
        <div class="usage-item">
          <span class="usage-label">Tokens</span>
          <span class="usage-value">{{ formatNumber(usage24h.tokens) }}</span>
        </div>
        <div class="usage-item">
          <span class="usage-label">Cost Estimate</span>
          <span class="usage-value">{{ formatCurrency(usage24h.cost_estimate_usd) }}</span>
        </div>
      </div>
    </div>

    <!-- Core Moat v1: Optimization Savings (Last 24h) -->
    <div class="card" *ngIf="optimizationSavings.length > 0">
      <div class="card-header">
        <h2>Optimization Savings (Last 24h)</h2>
        <a routerLink="/analytics" class="btn btn-sm btn-secondary">Session analytics</a>
      </div>
      <div class="optimization-savings-grid">
        <div *ngFor="let opt of optimizationSavings" class="optimization-savings-card">
          <div class="savings-card-header">
            <h3>{{ opt.name }}</h3>
            <span class="runs-badge">{{ formatNumber(opt.runs_count) }} runs</span>
          </div>
          <div class="savings-amount">
            <span class="savings-label">Tokens Saved</span>
            <span class="savings-value">{{ formatNumber(opt.tokens_saved) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Top Models & Policies -->
    <div class="grid-2">
      <div class="card">
        <div class="card-header">
          <h2>Top Models Used</h2>
        </div>
        <div *ngIf="topModels.length === 0" class="empty-state-small">
          <p>No model usage data yet</p>
        </div>
        <div *ngIf="topModels.length > 0" class="list">
          <div *ngFor="let model of topModels" class="list-item">
            <span class="list-label">{{ model.model }}</span>
            <span class="list-value">{{ formatNumber(model.count) }} calls</span>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-header">
          <h2>Top Policies Triggered</h2>
        </div>
        <div *ngIf="topPolicies.length === 0" class="empty-state-small">
          <p>No policies configured yet</p>
        </div>
        <div *ngIf="topPolicies.length > 0" class="list">
          <div *ngFor="let policy of topPolicies" class="list-item">
            <span class="list-label">{{ policy.name }}</span>
            <span class="list-value">{{ formatNumber(policy.trigger_count) }} triggers</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Recent Runs -->
    <div class="card">
      <div class="card-header">
        <h2>Recent Runs</h2>
        <a routerLink="/analytics" class="btn btn-sm btn-secondary">View savings</a>
      </div>
      
      <div *ngIf="!hasRuns" class="empty-state">
        <h3>No runs yet</h3>
        <p>Send your first run to see it appear here.</p>
        <a routerLink="/integrations/openclaw" class="btn btn-primary">OpenClaw setup</a>
      </div>

      <div *ngIf="hasRuns" class="runs-table">
        <table>
          <thead>
            <tr>
              <th>Run ID</th>
              <th>Type</th>
              <th>Source</th>
              <th>Model</th>
              <th>Status</th>
              <th>Time</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let run of recentRuns">
              <td><code>{{ run.id.substring(0, 8) }}...</code></td>
              <td>
                <span class="badge" [class.badge-agent]="run.type === 'agent'" [class.badge-chat]="run.type === 'chat'">
                  {{ run.type }}
                </span>
              </td>
              <td>
                <span class="badge badge-source">{{ run.source }}</span>
              </td>
              <td>{{ run.model }}</td>
              <td>{{ run.status }}</td>
              <td>{{ formatDate(run.created_at) }}</td>
              <td>
                <a routerLink="/analytics" class="btn-link">Savings</a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="card">
      <div class="card-header">
        <h2>Quick Actions</h2>
      </div>
      <div class="actions-grid">
        <a routerLink="/integrations/openclaw" class="action-card">
          <div class="action-icon"><span class="material-icons">add_circle</span></div>
          <h3>OpenClaw integration</h3>
          <p>Skill + local companion</p>
        </a>
        <a routerLink="/analytics" class="action-card">
          <div class="action-icon"><span class="material-icons">bar_chart</span></div>
          <h3>Savings analytics</h3>
          <p>Trends and usage</p>
        </a>
        <a routerLink="/billing" class="action-card">
          <div class="action-icon"><span class="material-icons">credit_card</span></div>
          <h3>Plan &amp; billing</h3>
          <p>Subscription and seats</p>
        </a>
      </div>
    </div>
  </div>
</div>
